import { Asset, Entry, ReferencedEntry } from 'contentful';

import { CookieConfig } from 'src/services/analytics/CookieConfig';
import { getContentTypeLink } from 'src/utils/EntryUtil';
import { HasBackgroundColor, HasImage, HasTitle } from './contentTypes.cloverdotcom';
import { Entries, Field } from './utilTypes';

export enum ShopContentType {
  SPEED_BUMP_LIST = 'resellerSpeedbump',
  SPEED_BUMP_RESELLER = 'speedBumpReseller',
  SALES_CHANNEL = 'salesChannel',
  CLIENT_CONFIG = 'clientConfig',
}

export enum ProductType {
  SHIPPING = 'SHIPPING',
  TERMINAL = 'TERMINAL',
  ACQUIRING = 'ACQUIRING',
  PROMOTION = 'PROMOTION',
}

export enum InstallmentPlanType {
  IP3 = 'IP3',
  IP6 = 'IP6',
  IP12 = 'IP12',
}

export enum FeeType {
  UNIT = 'UNIT',
  RECURRING = 'RECURRING',
  TRANSACTION = 'TRANSACTION',
}

export function getContentType(entry?: ReferencedEntry<any>): ShopContentType | undefined {
  const link = getContentTypeLink(entry);
  return link && link.id as ShopContentType;
}

export function is<T>(contentType: ShopContentType, maybe?: Entry<any>): maybe is Entry<T>;
export function is<T>(contentType: ShopContentType, maybe?: ReferencedEntry<any>): maybe is ReferencedEntry<T>;
export function is<T>(contentType: ShopContentType, maybe?: Entry<any> | ReferencedEntry<any>): maybe is Entry<T> | ReferencedEntry<T> {
  return getContentType(maybe) === contentType;
}

export function as<T>(contentType: ShopContentType, entry?: Entry<any>): Entry<T> | undefined;
export function as<T>(contentType: ShopContentType, entry?: ReferencedEntry<any>): ReferencedEntry<T> | undefined;
export function as<T>(contentType: ShopContentType, entry?: Entry<any> | ReferencedEntry<any>): Entry<T> | ReferencedEntry<T> | undefined {
  return is<T>(contentType, entry) && entry || undefined;
}

export type HasName = Field<{name: string}>;

export type HasHeadingText = Field<{headingText: string}>;

export type SpeedBumpList = HasName & HasHeadingText & Field<{
  salesChannels: Entries<SpeedBumpReseller>;
  wideSalesChannel: ReferencedEntry<SpeedBumpReseller>;
  wideSalesChannelLabel: string;
}>;

export type SpeedBumpReseller = HasName & HasHeadingText & Field<{
  externalRedirectUrl: string;
  redirectPageBlurb: string;
  speedbumpLogo: Asset;
  salesChannel: ReferencedEntry<SalesChannel>;
}>;

export type BrandingBanner = HasName & HasTitle & Field<{
  descriptionBlurb: string;
  iconImage: Asset;
}>;

export type SalesChannel = HasName & Field<{
  brandingBanner: ReferencedEntry<BrandingBanner>;
  cloverResellerId: string;
  nonProcessingResellerId: string;
  installmentPlan: string;
  pricingSchedule: Entries<SalesChannelPricing>;
  referrerDomains: string[];
  legalDisclosureFooterBlurb: string;
  currentPromotion: ReferencedEntry<SalesChannelPromotion>;
  phoneNumber: string;
  referralId?: string;
  liveChatType?: 'INTERCOM';
  shoppingFooter?: ReferencedEntry<ShopTiledContainer>;
  isDemoAvailable?: boolean;
  showMsrp?: boolean;
}>;

export type SalesChannelPromotion = HasName & Field<{
  detailsAndLegal: string;
  bannerEyebrow: string;
  bannerText: string;
  promoPricing: ReferencedEntry<SalesChannelPricing>;
}>;

export type SalesChannelPricing = HasName & Field<{
  price: number;
  rate: number;
  product: ReferencedEntry<Product>;
  plan: ReferencedEntry<ShopSubscriptionPlan>;
  feeType: FeeType;
}>;

export type ShopSubscriptionPlan = HasName & Field<{
  planId: string;
}>;

export type Product = HasName & Field<{
  firstDataMarketplaceId: string;
  firstDataMarketplaceCartDetailProductType: ProductType;
}>;

export type ShopTiledContainer = HasName & HasTitle & HasBackgroundColor & Field<{
  tiles: Entries<ShopTile>;
}>;

export type ShopTile = HasName & HasImage & HasTitle & Field<{
  descriptionBlurb: string;
}>;

export type ClientConfig = HasName & Field<{
  cookieConfig: CookieConfig[];
}>;
